<script lang="ts">
  /**
   * A "pick several from a shared list, or type a new one" field.
   *
   * Subcontractors, equipment and personnel are the same shape of question — which of
   * these does this activity need — over three different project-wide pick-lists, so
   * they share one control rather than three copies of it. Adding a new entry puts it
   * on both this activity *and* the project's list, which is the only way a name typed
   * once on site becomes available to the next person; "Manage list" removes it from
   * the list for good, without touching any activity that already has it assigned.
   *
   * All state here is local to one open modal — the component is destroyed with it, so
   * a half-typed entry doesn't survive into the next activity someone opens.
   */
  export let label: string;
  /** What this field holds, bound back to the form. */
  export let selected: string[];
  /** The project-wide pick-list to choose from. */
  export let known: string[];
  export let readOnly = false;
  /** Placeholder for the free-text "add a new one" input — the field's own examples. */
  export let newPlaceholder = "";
  /** Singular noun for the screen-reader labels, e.g. "subcontractor". */
  export let itemNoun: string;
  /** Given a name, its assigned color — subcontractors carry one, the others don't. */
  export let colorOf: ((name: string) => string) | null = null;
  export let onAddKnown: (name: string) => void;
  export let onRemoveKnown: (name: string) => void;

  let addValue = "";
  let newValue = "";
  let showManage = false;

  $: available = known.filter((k) => !selected.includes(k));

  function addExisting() {
    if (!addValue || selected.includes(addValue)) return;
    selected = [...selected, addValue];
    addValue = "";
  }

  /** A name typed here joins the project's list as well as this activity. */
  function addNew() {
    const name = newValue.trim();
    if (!name || selected.includes(name)) return;
    onAddKnown(name);
    selected = [...selected, name];
    newValue = "";
  }

  function remove(name: string) {
    selected = selected.filter((s) => s !== name);
  }
</script>

<div class="field-block">
  <span class="field-label">{label} <span class="opt-tag">(optional)</span></span>
  <div class="chip-list">
    {#each selected as name (name)}
      <span class="chip {colorOf ? 'sub' : 'pred'}" style={colorOf ? `background:${colorOf(name)}` : ""}>
        {name}
        {#if !readOnly}<button type="button" aria-label="Remove {itemNoun} {name}" on:click={() => remove(name)}>✕</button>{/if}
      </span>
    {/each}
  </div>
  {#if !readOnly}
    <div class="add-inline">
      <select bind:value={addValue} aria-label="Add an existing {itemNoun}">
        <option value="">+ Add existing…</option>
        {#each available as name}
          <option value={name}>{name}</option>
        {/each}
      </select>
      <button class="btn" type="button" disabled={!addValue} on:click={addExisting}>Add</button>
    </div>
    <div class="add-inline">
      <input
        type="text"
        placeholder={newPlaceholder}
        aria-label="Add a new {itemNoun}"
        bind:value={newValue}
        on:keydown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNew(); } }}
      />
      <button class="btn" type="button" disabled={!newValue.trim()} on:click={addNew}>Add new</button>
    </div>
    <button class="btn manage-toggle" type="button" on:click={() => showManage = !showManage}>
      {showManage ? "Hide list" : "Manage list…"}
    </button>
    {#if showManage}
      <div class="chip-list">
        {#each known as name (name)}
          <span class="chip pred">
            {name}
            <button type="button" aria-label="Delete {name} from the list" title="Remove from dropdown for good"
              on:click={() => onRemoveKnown(name)}>✕</button>
          </span>
        {/each}
      </div>
    {/if}
  {/if}
</div>
